import type { Route } from './+types/services.monthly';
import { Link } from 'react-router';
import { LeafMark } from '~/components/marketing/leaf-mark';
import { Breadcrumbs, breadcrumbSchemaItems } from '~/components/breadcrumbs';
import { RelatedPages } from '~/components/related-pages';
import { buildMeta, schemaFor, breadcrumbSchema } from '~/lib/seo';
import { SITE, PRICING } from '~/data/site';
import { useBranding } from '~/lib/branding';

export const meta: Route.MetaFunction = () => [
  ...buildMeta({
    title: 'Monthly Cleaning Service | Tampa Bay',
    description:
      `Monthly home cleaning (every 4 weeks) in Tampa Bay. Same crew, reliable schedule. Per-square-foot pricing starts at ${PRICING.monthly4Rate.toFixed(2)}/sqft. ${PRICING.minimum} minimum. Schedule your first clean today.`,
    path: '/monthly',
    jsonLd: [
      schemaFor('/monthly'),
      breadcrumbSchema(breadcrumbSchemaItems('/monthly')),
    ].filter((s): s is Record<string, unknown> => !!s),
  }),
];

export default function MonthlyServicePage() {
  const branding = useBranding();
  const examplePrice = 2400 * PRICING.monthly4Rate;

  return (
    <main id="main-content">
      <Breadcrumbs path="/monthly" pageLabel="Monthly Cleaning" />
      {/* ── HEADER ───────────────────────────────────────── */}
      <section className="section">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">Recurring Service</p>
          <h1 className="display-lg page-h1-full">
            Clean every four weeks,
            <em className="italic-display">the sustainable choice.</em>
          </h1>
          <p className="lede chapter-lede">
            Monthly cleaning keeps your home on a healthy rhythm without frequent visits. Your
            crew arrives every four weeks, same team each time. You get professional cleaning
            without breaking the schedule.
          </p>
        </div>
      </section>

      {/* ── PRICING ───────────────────────────────────────── */}
      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <h2 className="display-md">How much does it cost?</h2>
          <div className="price-example price-example-mt reveal">
            <h3 className="display-sm text-bone">${PRICING.monthly4Rate.toFixed(2)} per square foot</h3>
            <div className="price-ex-line price-ex-block-mt">
              <span>Example: 2,400 sqft home<br /><small className="text-mute">Your actual price depends on your home&rsquo;s size.</small></span>
              <span className="price-ex-total price-ex-total-sm">${Math.round(examplePrice)}</span>
            </div>
            <div className="price-ex-line">
              <span>Minimum on every service<br /><small className="text-mute">Even your smallest visits are a full professional clean.</small></span>
              <span className="price-ex-total price-ex-total-sm">${PRICING.minimum}</span>
            </div>
            <p className="marginalia price-example-marginalia">
              See your exact price for your home in under 60 seconds.
            </p>
          </div>
          <div className="section-cta-left">
            <Link to="/quote" className="btn btn-brass">Calculate your price →</Link>
          </div>
        </div>
      </section>

      {/* ── WHY MONTHLY ───────────────────────────────────── */}
      <section className="section-sm">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">The monthly advantage</p>
          <h2 className="display-md">One visit a month, <em className="italic-display">all in.</em></h2>
          <div className="feature-list reveal">
            <div className="feature-item">
              <h3 className="feature-title">Minimal disruption</h3>
              <p className="feature-text">
                One day a month. Your routine stays yours. No frequent schedule coordination.
                Just one reliable date you can mark on your calendar.
              </p>
            </div>
            <div className="feature-item">
              <h3 className="feature-title">Same crew knows you</h3>
              <p className="feature-text">
                Four weeks between visits means your team has time to plan ahead. They know
                what needs deep attention. They come prepared.
              </p>
            </div>
            <div className="feature-item">
              <h3 className="feature-title">Best long-term investment</h3>
              <p className="feature-text">
                Monthly cleaning costs less per visit than weekly or biweekly because the crew
                works efficiently. You still get the same crew continuity.
              </p>
            </div>
            <div className="feature-item">
              <h3 className="feature-title">Comprehensive rotating focus</h3>
              <p className="feature-text">
                Each visit has a deep focus area. Over four months, your entire home gets
                reset. Nothing ever slides.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHAT'S INCLUDED ───────────────────────────────── */}
      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <h2 className="display-md">What we clean every visit</h2>
          <div className="checklist-grid reveal">
            <div className="checklist-col">
              <h3 className="checklist-heading">Kitchen</h3>
              <ul className="checklist">
                <li>Countertops and backsplash</li>
                <li>Sink and faucet</li>
                <li>Appliances (exterior)</li>
                <li>Cabinet fronts and handles</li>
                <li>Baseboards</li>
              </ul>
            </div>
            <div className="checklist-col">
              <h3 className="checklist-heading">Bathrooms</h3>
              <ul className="checklist">
                <li>Toilets and sinks</li>
                <li>Mirrors and fixtures</li>
                <li>Shower/tub clean</li>
                <li>Floors and grout</li>
                <li>Ventilation fans</li>
              </ul>
            </div>
            <div className="checklist-col">
              <h3 className="checklist-heading">Living Spaces</h3>
              <ul className="checklist">
                <li>Vacuuming and mopping</li>
                <li>Dusting surfaces</li>
                <li>Light switches and doors</li>
                <li>Windows (interior)</li>
                <li>Baseboards and trim</li>
              </ul>
            </div>
            <div className="checklist-col">
              <h3 className="checklist-heading">Bedrooms</h3>
              <ul className="checklist">
                <li>Floors vacuumed</li>
                <li>Dusting all surfaces</li>
                <li>Organizing clutter</li>
                <li>Baseboards</li>
                <li>Light switches</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── FIRST VISIT UPGRADE ──────────────────────────── */}
      <section className="section-sm">
        <div className="container-narrow">
          <h2 className="display-md">Your first month is comprehensive.</h2>
          <p className="lede chapter-lede">
            Add a deep-clean upgrade to your first monthly visit: <strong>+${PRICING.deepUpgradeRate.toFixed(2)}/sqft</strong>.
            That&rsquo;s when we reset everything &mdash; baseboards, inside every cabinet, behind
            appliances, deep trim attention, all the spaces a rotating schedule needs time for.
            After that, your crew tackles one major focus area each month.
          </p>
          <p className="lede chapter-lede chapter-lede-mt">
            Example: 2,400 sqft home, first visit would be ${Math.round(examplePrice + (2400 * PRICING.deepUpgradeRate))}.
          </p>
        </div>
      </section>

      {/* ── WHY NLC ────────────────────────────────────────── */}
      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">Why {branding.name}</p>
          <h2 className="display-md">Professional care on <em className="italic-display">your schedule.</em></h2>
          <div className="value-props reveal">
            <div className="value-prop">
              <h3>W-2 Employees Only</h3>
              <p>Your crew is employed by us. Benefits, training, accountability — all real.</p>
            </div>
            <div className="value-prop">
              <h3>Insured &amp; Background Checked</h3>
              <p>Every technician vetted. Insurance covers your home. You have recourse.</p>
            </div>
            <div className="value-prop">
              <h3>Per-Square-Foot Pricing</h3>
              <p>No surprises. No upsells. The price is the price, every month.</p>
            </div>
            <div className="value-prop">
              <h3>Strategic Rotating Focus</h3>
              <p>Four weeks between visits means deep areas get real attention. Nothing rushed.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────── */}
      <RelatedPages path="/monthly" />

      <section className="cta-final">
        <div className="container-narrow cta-inner">
          <p className="cta-eyebrow">Ready to start?</p>
          <h2>Schedule your first monthly clean</h2>
          <p className="cta-lede">See your price for your home. No obligation. Takes 60 seconds.</p>
          <Link to="/quote" className="btn btn-brass btn-lg">Get your price →</Link>
        </div>
      </section>
    </main>
  );
}
